import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import NavTabs from '../../components/navtabs/NavTabs';
import { resolveImageUrl } from '../../utils/imageHelper';

interface Info {
  id: number;
  title: string;
  description: string;
  date: string;
  image: string;
}

const Information: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedInfo, setSelectedInfo] = useState<Info | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const tabs = [
    { label: 'Mall News', path: '/tenant-relation/announcements' },
    { label: 'Tenant News', path: '/tenant-relation/Tenant-News' },
    { label: 'Information & Rules', path: '/tenant-relation/information' }
  ];

  const informationData: Info[] = [
    {
      id: 1,
      title: "Mall Operating Hours",
      description: "Monday - Sunday: 10:00 AM - 10:00 PM",
      date: "Sep. 18 2025",
      image: "/images/information (1).png"
    },
    {
      id: 2,
      title: "All tenants must comply with mall operating hours",
      description: "All tenants must comply with mall operating hours",
      date: "Sep. 18 2025",
      image: "/images/information (2).png"
    },
    {
      id: 3,
      title: "Maintain cleanliness and hygiene standards",
      description: "Maintain cleanliness and hygiene standards",
      date: "Sep. 18 2025",
      image: "/images/information (3).png"
    },
    {
      id: 4,
      title: "Follow safety protocols and emergency procedures",
      description: "Follow safety protocols and emergency procedures",
      date: "Sep. 18 2025",
      image: "/images/news (4).png"
    },
    {
      id: 5,
      title: "Report any maintenance issues immediately",
      description: "Report any maintenance issues immediately",
      date: "Sep. 18 2025",
      image: "/images/news (5).png"
    },
    {
      id: 6,
      title: "Emergency Procedures",
      description: "In case of emergency, follow evacuation routes and contact security immediately.",
      date: "Sep. 18 2025",
      image: "/images/news (6).png"
    },
    {
      id: 7,
      title: "Fire Safety",
      description: "Know the location of fire extinguishers and emergency exits in your area.",
      date: "Sep. 18 2025",
      image: "/images/news (7).png"
    },
    {
      id: 8,
      title: "Mall Management",
      description: "Phone: (021) 123-4567 Email: info@lippomallpuri.com",
      date: "Sep. 18 2025",
      image: "/images/news (8).png"
    },
    {
      id: 9,
      title: "Security",
      description: "Emergency: (021) 987-6543 24/7 Available",
      date: "Sep. 18 2025",
      image: "/images/news (9).png"
    }
  ];

  const itemsPerPage = 9;
  const totalPages = Math.max(1, Math.ceil(informationData.length / itemsPerPage));

  const getCurrentPageData = (): Info[] => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return informationData.slice(startIndex, startIndex + itemsPerPage);
  };

  const handlePageChange = (page: number) => {
    const safe = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(safe);
  };

  const handleCardClick = (info: Info) => {
    setSelectedInfo(info);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedInfo(null);
  };

  const renderPaginationNumbers = (): Array<number | string> => {
    const pages: Array<number | string> = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2, 3, 4, '...', totalPages);
    }

    return pages;
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-end">
          <NavTabs tabs={tabs} />
        </div>

        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {getCurrentPageData().map((info) => (
            <div
              key={info.id}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleCardClick(info)}
            >
              
              <div className="h-48 bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <img
                  src={resolveImageUrl(info.image)}
                  alt={info.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const nextSibling = target.nextElementSibling as HTMLElement | null;
                    if (nextSibling) nextSibling.style.display = 'flex';
                  }}
                />
                
                <div className="w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center hidden">
                  <div className="text-gray-400 text-6xl opacity-50">ðŸ“„</div>
                </div>
              </div>

              
              <div className="p-6">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-3 line-clamp-2">
                  {info.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">
                  {info.description}
                </p>

                
                <div className="flex items-center text-gray-400 dark:text-gray-500 text-sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  {info.date}
                </div>
              </div>
            </div>
          ))}
        </div>

        
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {renderPaginationNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === 'number' && handlePageChange(page)}
                disabled={page === '...'}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === page
                    ? 'bg-blue-500 text-white'
                    : page === '...'
                    ? 'text-gray-400 cursor-default'
                    : 'text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      
      {isModalOpen && selectedInfo && (
        <div className="fixed inset-0 z-50">
          
          <div
            className="absolute inset-0 backdrop-blur-sm bg-black/20 dark:bg-black/40"
            onClick={closeModal}
          ></div>

          
          <div className="relative flex items-center justify-center min-h-screen p-4" onClick={closeModal}>
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-scroll modal-content relative shadow-2xl"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onClick={(e) => e.stopPropagation()}
            >
              <style>{`
                .modal-content::-webkit-scrollbar {
                  display: none;
                }
              `}</style>

              
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-10 bg-white dark:bg-gray-700 rounded-full p-2 shadow-lg hover:bg-white dark:hover:bg-gray-600 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>

              
              <div className="flex flex-col">
                
                <div className="w-full h-80 bg-gray-100 dark:bg-gray-700 overflow-hidden rounded-t-2xl relative">
                  <img
                    src={resolveImageUrl(selectedInfo.image)}
                    alt={selectedInfo.title}
                    className="w-full h-full object-contain"
                    onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      const nextSibling = target.nextElementSibling as HTMLElement | null;
                      if (nextSibling) nextSibling.style.display = 'flex';
                    }}
                  />
                  
                  <div className="w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center hidden rounded-t-2xl">
                    <div className="text-gray-400 text-6xl opacity-50">ðŸ“„</div>
                  </div>
                </div>

                
                <div className="p-8 bg-white dark:bg-gray-800 rounded-b-2xl">
                  
                  <h1 className="font-bold text-3xl text-gray-900 dark:text-white mb-6 leading-tight">
                    {selectedInfo.title}
                  </h1>

                  
                  <div className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-6">
                    <p className="mb-4">{selectedInfo.description}</p>

                    
                    {selectedInfo.id === 1 && (
                      <div className="space-y-3 text-base">
                        <p className="font-medium">Mall Operating Schedule:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>Monday to Thursday: 10:00 AM - 10:00 PM</li>
                          <li>Friday to Sunday: 10:00 AM - 10:00 PM</li>
                          <li>Public Holidays: 10:00 AM - 10:00 PM</li>
                        </ul>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">*Operating hours may change during special events or holidays. Please check with mall management for updates.</p>
                      </div>
                    )}

                    {selectedInfo.id === 6 && (
                      <div className="space-y-3 text-base">
                        <p className="font-medium">Emergency Procedures:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>Stay calm and follow instructions from security personnel</li>
                          <li>Use nearest emergency exit routes</li>
                          <li>Do not use elevators during emergencies</li>
                          <li>Gather at designated assembly points</li>
                          <li>Contact emergency services: 911 or (021) 987-6543</li>
                        </ul>
                      </div>
                    )}

                    {selectedInfo.id === 8 && (
                      <div className="space-y-3 text-base">
                        <p className="font-medium">Contact Information:</p>
                        <div className="space-y-2">
                          <p><strong>Phone:</strong> (021) 123-4567</p>
                          <p><strong>Email:</strong> info@lippomallpuri.com</p>
                          <p><strong>Address:</strong> Lippo Mall Puri, Jakarta Barat</p>
                          <p><strong>Office Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM</p>
                        </div>
                      </div>
                    )}
                  </div>

                  
                  <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm border-t dark:border-gray-600 pt-4">
                    <Calendar className="w-4 h-4 mr-2" />
                    {selectedInfo.date}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Information;
